/* globals window, document */
import { reactive } from 'vue'
import { is } from './util'

function loadScript(appId) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src*="widget.intercom.io/widget/${appId}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    script.onload = resolve
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript.parentNode.insertBefore(script, firstScript)
  })
}

export default {
  install(app, { appId }) {
    const state = reactive({
      ready: false,
      visible: false,
      unreadCount: 0
    })

    let loadPromise = null

    const ensureLoaded = () => {
      if (state.ready) return Promise.resolve()
      if (!loadPromise) {
        // Set up the Intercom placeholder queue so calls made before
        // the script loads are buffered and replayed automatically
        if (typeof window.Intercom !== 'function') {
          const placeholder = (...args) => placeholder.c(args)
          placeholder.q = []
          placeholder.c = args => placeholder.q.push(args)
          window.Intercom = placeholder
        }
        loadPromise = loadScript(appId).then(() => {
          state.ready = true
          window.Intercom('onHide', () => (state.visible = false))
          window.Intercom('onShow', () => (state.visible = true))
          window.Intercom('onUnreadCountChange', count => (state.unreadCount = count))
        })
      }
      return loadPromise
    }

    const callIntercom = (...args) => window.Intercom(...args)

    const intercom = {
      get ready() { return state.ready },
      get visible() { return state.visible },
      get unreadCount() { return state.unreadCount },

      _call: callIntercom,

      async boot(options = { app_id: appId }) {
        await ensureLoaded()
        if (!options.app_id) options.app_id = appId
        callIntercom('boot', options)
      },
      shutdown: () => { if (state.ready) callIntercom('shutdown') },
      update: (...options) => { if (state.ready) callIntercom('update', ...options) },
      showArticle: (article) => { if (state.ready) callIntercom('showArticle', article) },
      async show() {
        await ensureLoaded()
        callIntercom('show')
      },
      hide: () => { if (state.ready) callIntercom('hide') },
      showMessages: () => { if (state.ready) callIntercom('showMessages') },
      async showNewMessage(content) {
        await ensureLoaded()
        callIntercom('showNewMessage', ...(is(String, content) ? [content] : []))
      },
      trackEvent: (name, ...metadata) => { if (state.ready) callIntercom('trackEvent', ...[name, ...metadata]) },
      getVisitorId: () => state.ready ? callIntercom('getVisitorId') : null
    }

    app.config.globalProperties.$intercom = intercom
  }
}
